import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Sun, ArrowRight, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import satellite from "@/assets/satellite.jpg";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const askLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password aggiornata");
      window.location.href = "/login";
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f3f0] text-zinc-900 p-3 sm:p-4 flex gap-4">
      {/* Left image panel */}
      <div className="hidden lg:flex relative flex-1 rounded-[28px] overflow-hidden">
        <img src={satellite} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/35" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <Link to="/" className="flex items-center gap-2.5 w-max">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-zinc-900"><Sun size={18} strokeWidth={2.5} /></div>
            <span className="font-semibold text-lg">SolarFlow</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-semibold leading-[1.15] tracking-tight max-w-md">
              {token ? "Scegli una nuova password e torna al lavoro." : "Capita a tutti. Recupera l'accesso in un istante."}
            </h2>
            <p className="mt-5 text-white/80 text-sm max-w-md">
              {token
                ? "Imposta una password sicura: ti basteranno pochi secondi."
                : "Ti invieremo un link sicuro per reimpostare la password."}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-[460px] flex items-center justify-center px-2 sm:px-6 py-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center text-white"><Sun size={20} strokeWidth={2.5} /></div>
            <span className="font-semibold text-xl">SolarFlow</span>
          </Link>

          {token ? (
            /* Reset with token */
            <>
              <h1 className="text-3xl font-semibold tracking-tight">Nuova password.</h1>
              <p className="text-zinc-500 mt-2 mb-8">Imposta la password che userai per accedere.</p>

              <form onSubmit={reset} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700">Nuova password</label>
                  <input
                    type="password" minLength={6} required value={pw} onChange={(e) => setPw(e.target.value)}
                    placeholder="Minimo 6 caratteri" data-testid="new-password-input"
                    className="w-full mt-1.5 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm"
                  />
                </div>
                <button
                  type="submit" disabled={busy} data-testid="reset-submit-btn"
                  className="w-full bg-zinc-900 text-white font-semibold rounded-full py-3.5 flex items-center justify-center gap-2 hover:bg-zinc-800 active:scale-[0.99] transition-all disabled:opacity-50"
                >
                  {busy ? "Salvataggio…" : "Imposta password"} <ArrowRight size={16} />
                </button>
              </form>
            </>
          ) : sent ? (
            /* Link sent confirmation */
            <>
              <div className="w-12 h-12 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-6"><MailCheck size={22} /></div>
              <h1 className="text-3xl font-semibold tracking-tight">Controlla la posta.</h1>
              <p className="text-zinc-500 mt-2">
                Se esiste un account per <span className="font-medium text-zinc-700">{email}</span>, riceverai un link per reimpostare la password.
              </p>
              <p className="text-xs text-zinc-400 mt-4">In modalità demo trovi il token nei log del server.</p>
              <button
                onClick={() => setSent(false)}
                className="mt-8 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                Non hai ricevuto nulla? Riprova
              </button>
            </>
          ) : (
            /* Request reset link */
            <>
              <h1 className="text-3xl font-semibold tracking-tight">Password dimenticata?</h1>
              <p className="text-zinc-500 mt-2 mb-8">Inserisci la tua email e ti invieremo un link per reimpostarla.</p>

              <form onSubmit={askLink} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700">Email</label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="mario@energia.it" data-testid="forgot-email-input"
                    className="w-full mt-1.5 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm"
                  />
                </div>
                <button
                  type="submit" disabled={busy} data-testid="forgot-submit-btn"
                  className="w-full bg-zinc-900 text-white font-semibold rounded-full py-3.5 flex items-center justify-center gap-2 hover:bg-zinc-800 active:scale-[0.99] transition-all disabled:opacity-50"
                >
                  {busy ? "Invio…" : "Invia link"} <ArrowRight size={16} />
                </button>
              </form>
            </>
          )}

          <div className="mt-6">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="back-to-login-link">
              <ArrowLeft size={14} /> Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
